import { useCallback, useEffect, useState } from 'react';

import { hasNativeShare } from 'src/shared/helpers/environment';
import { imageUrlToFile } from 'src/shared/helpers/image';
import { logError } from 'src/shared/telemetry/logger';

interface UseImageNativeShareParams {
  imageUrl: string;
  imageName?: string;
  title?: string;
  text?: string;
  onShareFail?: () => void;
}
export const useImageNativeShareHandler = ({
  imageUrl,
  imageName,
  title,
  text,
  onShareFail,
}: UseImageNativeShareParams) => {
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState<File>();

  useEffect(() => {
    if (!hasNativeShare()) {
      return;
    }

    void (async () => {
      if (!imageUrl) {
        return;
      }
      try {
        const imageFile = await imageUrlToFile(imageUrl, imageName);
        setFile(imageFile);
      } catch (err) {
        setFile(undefined);
        logError(err as Error);
      }
    })();
  }, [imageUrl, imageName]);

  return useCallback(() => {
    const fileDownloadFailed = !file;
    const unableToNativelyShareFile =
      isOpen || fileDownloadFailed || !hasNativeShare();
    if (unableToNativelyShareFile) {
      if (fileDownloadFailed) {
        onShareFail?.();
      }
      return;
    }

    setIsOpen(true);
    void (async () => {
      try {
        await navigator.share({
          title,
          text,
          files: [file],
        });
      } catch (err) {
        const isAbortError =
          err instanceof DOMException && err.name === 'AbortError';
        if (!isAbortError) {
          logError(err as Error);
        }
      } finally {
        setIsOpen(false);
      }
    })();
  }, [file, isOpen, text, title, onShareFail]);
};
